import type { ReactNode } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { CheckIcon, MessageCircleIcon, ShieldCheckIcon, TruckIcon } from 'lucide-react';
import { founder, salon } from '../../data/content';
import { usePages } from '../../contexts/SiteContext';
import { policyCopy } from '../../data/policies';
import { shopLabels } from '../../data/shopLabels';
import { euro, rating } from '../../core/format/currency';
import { plural } from '../../core/format/text';
import { ease } from '../../core/format/motion';
import { activePromo } from '../../core/rules/promo';
import { canSubscribe } from '../../core/rules/features';
import { useFeatures } from '../../hooks/useFeatures';
import type { Product } from '../../core/types/catalog';
import { btnPrice, btnPriceAmount, btnSecondary } from '../../ui/styles';
// Sterren volgen het echte gemiddelde: een 4,2 toont vier volle sterren, niet vijf.
import { Stars } from '../shared/Stars';
import { oneOffMeta, refillMeta } from './productRules';
type Props = {
  product: Product;
  soldOut: boolean;
  subscribe: boolean;
  onSubscribe: (value: boolean) => void;
  added: boolean;
  onAdd: () => void;
  /** Na toevoegen: de mand openen (de volgende logische stap). */
  onViewCart: () => void;
  giftHint: string | null;
  /** Hieronder: de uitklapblokken met waarom, gebruik en ingrediënten. */
  children: ReactNode;
};

/** Merk, naam, beoordeling, eenmalig of bijvullen, en de knop. */
export function ProductBuyBox({
  product,
  soldOut,
  subscribe,
  onSubscribe,
  added,
  onAdd,
  onViewCart,
  giftHint,
  children
}: Props) {
  const pages = usePages();
  const reduce = useReducedMotion();
  const features = useFeatures();
  const promo = activePromo(product);
  const options = [{
    value: false,
    title: 'Eenmalig',
    meta: oneOffMeta(product)
  }, ...(canSubscribe(features, product) ? [{
    value: true,
    title: `Automatisch bijvullen · ${pages.subscription.name}`,
    meta: refillMeta(product, policyCopy.subscriptionPerks)
  }] : [])];
  return <div className="lg:sticky lg:top-28" data-id="element-8016" data-mp="18w572bzkvoum">
      <p className="text-[14px] text-clay" data-id="element-8017" data-mp="18wx29lp9jkkd">{product.brand}</p>
      <h1 className="mt-2 font-display text-[2.4rem] font-light leading-[1.05] tracking-[-0.01em] text-ink sm:text-5xl" data-id="element-8018" data-mp="13yc21ma0h0vx">{product.name}</h1>
      {product.reviews > 0 && <a href="#reviews" className="mt-4 inline-flex items-center gap-2 text-[14px] text-muted transition-colors duration-150 ease-soft hover:text-ink" data-id="element-8019" data-mp="1cyi9aqf494mn">
          <Stars value={product.rating} data-id="element-8020" data-mp="1rty8f9ujzqpw" />
          <span className="sr-only" data-id="element-8021" data-mp="1mwoa14p6s4px">{shopLabels.rating(product)}</span>
          <span aria-hidden="true" data-id="element-8022" data-mp="1mubvl1in0chm">
            {rating(product.rating)} · {plural(product.reviews, 'beoordeling', 'beoordelingen')}
          </span>
        </a>}
      {product.note && <p className="mt-5 text-[16px] leading-relaxed text-muted" data-id="element-8023" data-mp="1wmr9nnqtcgx8">{product.note}</p>}

      {promo && <p className="mt-5 bg-blush px-4 py-3 text-[14px] leading-relaxed text-ink" data-id="element-8024" data-mp="2ama6hke6c5u8">
          <span className="font-medium" data-id="element-8025" data-mp="25oh3t1c2xtyp">{promo.label}</span>
          {promo.until && <> — {promo.until}</>}
        </p>}

      {/* Alleen een keuze tonen als er iets te kiezen is: zonder bijvullen is "Eenmalig" de enige optie. */}
      {options.length > 1 ? <fieldset className="mt-7 space-y-2" data-id="element-8026" data-mp="2fbbauv6wr9hx">
          <legend className="sr-only" data-id="element-8027" data-mp="2fcmdgromab7g">Aankoopoptie</legend>
          {options.map(o => {
        const active = subscribe === o.value;
        return <label key={o.title} className={`flex cursor-pointer items-start gap-4 border p-4 transition-colors duration-150 ease-soft [&:has(:focus-visible)]:outline [&:has(:focus-visible)]:outline-2 [&:has(:focus-visible)]:outline-offset-2 [&:has(:focus-visible)]:outline-clay ${active ? 'border-ink bg-shell' : 'border-line hover:border-ink/40'}`} data-id="element-8028" data-mp="26h6mcif8ya5k">
                <input type="radio" name="purchase" checked={active} onChange={() => onSubscribe(o.value)} className="sr-only" data-id="element-8029" data-mp="1nlgjyu3ejbr5" />
                <span aria-hidden="true" className={`mt-1 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${active ? 'border-ink' : 'border-line'}`} data-id="element-8030" data-mp="1khhg98ysvhh7">
                  {active && <span className="h-2 w-2 rounded-full bg-ink" data-id="element-8031" data-mp="1arxxs4f01pmv" />}
                </span>
                <span className="flex-1" data-id="element-8032" data-mp="1apl62zjfw0wm">
                  <span className="flex items-baseline justify-between gap-3" data-id="element-8033" data-mp="15tmmdp92372j">
                    <span className="text-[15px] text-ink" data-id="element-8034" data-mp="11qztdl0nfg3r">{o.title}</span>
                    <span className="font-display text-lg text-ink" data-id="element-8035" data-mp="11owro3q4vyj7">{euro(product.price)}</span>
                  </span>
                  <span className="mt-1 block text-[13px] leading-relaxed text-muted" data-id="element-8036" data-mp="rstpwyz721ws">{o.meta}</span>
                </span>
              </label>;
      })}
        </fieldset> : <p className="mt-7 flex items-baseline justify-between gap-3 border-t border-line pt-4" data-id="element-8037" data-mp="2m6uorjuquowv">
          <span className="text-[13px] text-muted" data-id="element-8038" data-mp="2h89mzfzufx6b">{options[0]?.meta}</span>
          <span className="font-display text-2xl text-ink" data-id="element-8039" data-mp="2h5x5t51wtqub">{euro(product.price)}</span>
        </p>}

      {soldOut ? <div className="mt-5 space-y-2" data-id="element-8040" data-mp="2tqdrnani7cwd">
          <p className="text-center text-[14px] text-muted" data-id="element-8041" data-mp="2tpawurn28qb6">{shopLabels.soldOutShort}.</p>
          {/* Voorheen alleen tekst ("app Sarah"); nu een echte knop. */}
          <a href={salon.whatsapp} target="_blank" rel="noreferrer" className={`${btnSecondary} w-full`} data-id="element-8042" data-mp="33gylkks8ft8y">
            <MessageCircleIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-8043" data-mp="26qxzitj8zqvc" />
            Vraag {founder.firstName} om een alternatief
            <span className="sr-only" data-id="element-8044" data-mp="2as1cn89wv8sv">{shopLabels.viaWhatsApp}</span>
          </a>
        </div> : <button type="button" onClick={onAdd} className={`${btnPrice} mt-5`} data-id="element-8045" data-mp="2fsxj9tlnd91z">
          <AnimatePresence mode="wait" initial={false} data-id="element-8046" data-mp="2kriibpp5xmou">
            <motion.span key={added ? 'added' : 'add'} initial={reduce ? {
          opacity: 0
        } : {
          opacity: 0,
          y: 4
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={reduce ? {
          opacity: 0
        } : {
          opacity: 0,
          y: -4
        }} transition={{
          duration: 0.15,
          ease
        }} className={`flex w-full items-center gap-4 ${added ? 'justify-center' : 'justify-between'}`} data-id="element-8047" data-mp="3r6j79bfrt4lx">
              {added ? <span className="flex items-center gap-2" data-id="element-8048" data-mp="3gd23uaxjfkwq">
                  <CheckIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-8049" data-mp="3bh38u4hpf4nc" />
                  Toegevoegd
                </span> : <>
                  <span data-id="element-8050" data-mp="2irwap6m5wh2m">In winkelmand</span>
                  <span className={btnPriceAmount} data-id="element-8051" data-mp="2isxld9z9n1jt">{euro(product.price)}</span>
                </>}
            </motion.span>
          </AnimatePresence>
        </button>}

      {added && !soldOut ? <p className="mt-3 text-center text-[14px]" data-id="element-8052" data-mp="2snhb50bl19a9">
          <button type="button" onClick={onViewCart} aria-haspopup="dialog" className="min-h-[44px] text-ink underline underline-offset-4 hover:text-clay" data-id="element-8053" data-mp="24yy0wf8z2mfj">
            Bekijk je winkelmand
          </button>
        </p> : giftHint && !soldOut && <p className="mt-3 text-center text-[13px] text-clay" data-id="element-8054" data-mp="k9kgx6ulo589">{giftHint}</p>}

      <ul className="mt-6 space-y-2.5 border-t border-line pt-5 text-[14px] text-muted" data-id="element-8055" data-mp="kbni8n17dksa">
        <li className="flex items-center gap-2.5" data-id="element-8056" data-mp="5mhjf06a1wkx">
          <TruckIcon className="h-4 w-4 shrink-0 text-clay" strokeWidth={1.5} aria-hidden="true" data-id="element-8057" data-mp="aj7vlceyo812" />
          {policyCopy.delivery}
        </li>
        <li className="flex items-center gap-2.5" data-id="element-8058" data-mp="ya4zc1wlrz18">
          <ShieldCheckIcon className="h-4 w-4 shrink-0 text-clay" strokeWidth={1.5} aria-hidden="true" data-id="element-8059" data-mp="138yerzvga041" />
          {policyCopy.returns} — ook geopend
        </li>
        {!soldOut && <li className="flex items-center gap-2.5" data-id="element-8060" data-mp="t9z5qg6rw2gi">
            <MessageCircleIcon className="h-4 w-4 shrink-0 text-clay" strokeWidth={1.5} aria-hidden="true" data-id="element-8061" data-mp="t8o3iksz72ri" />
            <a href={salon.whatsapp} target="_blank" rel="noreferrer" className="underline-offset-4 transition-colors duration-150 ease-soft hover:text-ink hover:underline" data-id="element-8062" data-mp="39xl75z18y9la">
              Vraag {founder.firstName} of dit bij je past
              <span className="sr-only" data-id="element-8063" data-mp="4jtt8743vkrn">{shopLabels.viaWhatsApp}</span>
            </a>
          </li>}
      </ul>

      {children}
    </div>;
}